import { type FC, type HTMLAttributes, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import * as commonStyles from '@DS/components/components/foundations/shapes/card/styles'
import Loader from '@DS/components/components/loaders/loader/Loader'
import { useIsMediumUp } from '@Hooks/useResponsive'

import * as styles from '../dateSearch/styles'

import { type DateTimeButtonSize } from './DateTimeButton.types'
import RemoveInputButton from './RemoveInputButton'

export type DateTimeButtonProps = {
  placeholder?: string
  startIcon?: ReactNode
  buttonLabelId?: string
  value: string
  onClick: VoidFunction
  onClear?: VoidFunction
  isDisabled?: boolean
  isVisible?: boolean
  isWrapped?: boolean
  isCompactedOnMobile?: boolean
  isLoading?: boolean
  size?: DateTimeButtonSize
} & HTMLAttributes<HTMLButtonElement>

const DateTimeButton: FC<DateTimeButtonProps> = ({
  placeholder,
  startIcon,
  buttonLabelId = 'trip_outwardDateLabel',
  value,
  onClick,
  onClear,
  isDisabled = false,
  isVisible = false,
  isWrapped = false,
  isCompactedOnMobile = true,
  isLoading = false,
  size = 'medium',
  ...rest
}) => {
  const theme = useTheme()
  const isMediumUp = useIsMediumUp(theme)
  const { formatMessage } = useIntl()

  if (!isVisible) {
    return null
  }

  const isClearButtonVisible = !isDisabled && !!value && !!onClear && !isLoading

  const getValueLabel = () => {
    if (!isCompactedOnMobile || (isMediumUp && buttonLabelId)) {
      return (
        <span css={styles.label(theme, isDisabled)}>
          <FormattedMessage
            id={buttonLabelId}
            values={{ date: <span css={styles.value(theme, isDisabled)}>{value}</span> }}
          />
        </span>
      )
    }

    return <span css={styles.value(theme, isDisabled)}>{value}</span>
  }

  return (
    <div css={styles.buttonWrapper(theme, isWrapped)}>
      <button
        {...rest}
        css={styles.button(theme, !!value, isDisabled, size)}
        disabled={isDisabled}
        type="button"
        onClick={() => onClick()}
      >
        <div css={commonStyles.cardContentChildrenWrapperBase}>
          {startIcon}
          <Typography component="span">{value ? getValueLabel() : placeholder}</Typography>
        </div>
        {isLoading && <Loader css={styles.loadingIcon} aria-hidden />}
      </button>
      {isClearButtonVisible && (
        <RemoveInputButton
          css={styles.removeButton(theme)}
          onClick={(event) => {
            event.stopPropagation()
            onClear()
          }}
          aria-label={formatMessage({
            id: 'input_clearButton_inward_ariaLabel',
          })}
        />
      )}
    </div>
  )
}

export default DateTimeButton
